/*
 * Yurls Shop — meescrollende koopbalk.
 *
 * Verschijnt zodra de koopknop hierboven uit beeld is en verdwijnt weer zodra
 * je hem terugziet. De knop in de balk stuurt hetzelfde formulier als die knop
 * — er is dus maar één koopformulier, en het aantal kan niet uit de pas lopen.
 *
 * De balk toont ook hoever je bent richting de bundelkorting. Die telling moet
 * gelijk zijn aan die van de bundelbalk in assets/yurls-bundle-rail.js:
 * meetellen wat geld kost, dus original_price boven nul. Boekjes die gratis
 * zijn geworden dóór de bundel houden hun oorspronkelijke prijs en tellen mee;
 * boekjes die sowieso gratis zijn niet.
 *
 * Er stond hier eerder final_price boven nul, en dat telde te weinig: zodra de
 * korting het derde boekje wegstreepte zakte de teller van 3 naar 2 terwijl je
 * er wel degelijk drie had.
 *
 * GEEN BEDRAG IN DEZE BALK — niet vergeten, maar bewust weggehaald.
 * Hier stond een totaal dat simpelweg stukprijs × aantal rekende. Dat is fout
 * zodra de bundelkorting meedoet: bij 3 boekjes betaal je er 2. Het juiste
 * bedrag is hier ook niet te berekenen, om drie redenen:
 *   1. de kortingen gelden voor de hele winkelwagen, niet voor dit product,
 *      en deze pagina weet niet wat er al in de wagen ligt;
 *   2. bij "koop 2, krijg 1 gratis" bepaalt Shopify zelf welk exemplaar
 *      gratis wordt — doorgaans het goedkoopste, dus het bedrag hangt af van
 *      de samenstelling van de wagen;
 *   3. de treden stapelen, dus Shopify moet uitrekenen welke combinatie het
 *      gunstigst uitpakt.
 * Een bedrag hier neerzetten betekent Shopify's kortingsmotor namaken in
 * JavaScript, die stil uit de pas gaat lopen zodra iemand in de admin een
 * korting wijzigt. De voortgangsbalk hiernaast vertelt het verhaal wél goed:
 * die telt uit de echte winkelwagen. Zet er dus geen bedrag terug.
 *
 * Verwachte opbouw:
 *   <yurls-sticky-buy data-watch=".koopknop" data-goal="4">
 *     <div data-bar><div data-fill></div></div>
 *     <span data-count></span>
 *   </yurls-sticky-buy>
 */

if (!customElements.get('yurls-sticky-buy')) {
  customElements.define(
    'yurls-sticky-buy',
    class YurlsStickyBuy extends HTMLElement {
      connectedCallback() {
        this.fillEl = this.querySelector('[data-fill]');
        this.countEl = this.querySelector('[data-count]');
        this.goal = parseInt(this.dataset.goal, 10) || 4;

        this.watchVisibility();
        this.refresh();

        this.onCartEvent = () => setTimeout(() => this.refresh(), 400);
        document.addEventListener('cart:refresh', this.onCartEvent);
        document.addEventListener('cart-update', this.onCartEvent);

        // De koopknop meldt een geslaagde toevoeging via de pubsub van het
        // thema, niet met een gebeurtenis op het document. Zonder deze regel
        // blijft de teller in de balk staan op het aantal van bij het laden.
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS === 'object' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartEvent);
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('cart:refresh', this.onCartEvent);
        document.removeEventListener('cart-update', this.onCartEvent);
      }

      /*
       * Zichtbaar zodra de koopknop hierboven niet meer in beeld is, en meteen
       * weer weg zodra je hem terugziet — ook als je maar een randje ervan
       * ziet. Twee knoppen tegelijk tonen is verwarrend en de balk zou dan
       * ongevraagd de pagina afdekken.
       */
      watchVisibility() {
        this.target = this.dataset.watch
          ? document.querySelector(this.dataset.watch)
          : null;

        if (!this.target || !('IntersectionObserver' in window)) {
          // Kunnen we niet meten, dan maar altijd tonen — beter dan nooit.
          this.toggleAttribute('data-visible', true);
          return;
        }

        this.startObserver();

        // De balk is op een telefoon lager dan op een laptop, en dat bepaalt
        // hieronder de rekenmarge. Draait iemand zijn scherm, dan opnieuw.
        this.onResize = () => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.startObserver(), 200);
        };
        window.addEventListener('resize', this.onResize);
      }

      startObserver() {
        if (this.observer) this.observer.disconnect();

        // De balk zelf ligt over de onderkant van het scherm. Schuift de
        // koopknop daar precies onderdoor, dan telt hij voor de browser nog
        // als zichtbaar terwijl jij hem niet meer ziet — en zou de balk dus
        // net te laat komen. Daarom kijken we alleen naar het stuk scherm
        // boven de balk, en meten we die hoogte hier in plaats van hem vast
        // op te schrijven: op smalle schermen is de balk lager.
        var cover = Math.round(this.getBoundingClientRect().height) || 0;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.toggleAttribute('data-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -' + cover + 'px 0px' }
        );

        this.observer.observe(this.target);
      }

      refresh() {
        fetch('/cart.js')
          .then(function (response) {
            return response.json();
          })
          .then((cart) => {
            // Zie de toelichting bovenaan: original_price, niet final_price.
            var counted = cart.items.reduce(function (total, item) {
              return item.original_price > 0 ? total + item.quantity : total;
            }, 0);
            this.render(counted);
          })
          .catch(function () {});
      }

      render(counted) {
        if (this.fillEl) {
          var percentage = Math.min(100, Math.round((counted / this.goal) * 100));
          this.fillEl.style.width = percentage + '%';
        }

        if (this.countEl) {
          var template = this.countEl.dataset.template || '[aantal] boekjes';
          var single = this.countEl.dataset.templateSingle || '[aantal] boekje';
          var text = counted === 1 ? single : template;
          this.countEl.textContent = text.replace('[aantal]', counted);
        }
      }
    }
  );
}
